import { getAccessToken } from '@privy-io/react-auth'
import * as Sentry from '@sentry/nextjs'
import {
  CacheConfig,
  Environment,
  Network,
  RecordSource,
  RelayFeatureFlags,
  RequestParameters,
  Store,
  UploadableMap,
  Variables,
} from 'relay-runtime'

RelayFeatureFlags.ENABLE_RELAY_RESOLVERS = true

const API_URL = process.env.NEXT_PUBLIC_ZORA_UNIVERSAL_API_HTTP_URI!
if (!API_URL) {
  throw 'Missing API URL'
}

async function fetchFn(
  operation: RequestParameters,
  variables: Variables,
  _cacheConfig: CacheConfig,
  _uploadables?: UploadableMap | null
) {
  const config = {
    requestTimeout: 15000,
    retryDelay: 1000,
    maxRetries: 2,
  }

  // don't retry mutations
  const isMutation = operation.operationKind === 'mutation'
  const maxRetries = isMutation ? 0 : config.maxRetries
  // track retries and last error
  let retries = 0
  let lastError: Error | unknown = null
  do {
    try {
      const headers = await createHeaders()
      const signal = AbortSignal.timeout(config.requestTimeout)
      const response = await fetch(process.env.NEXT_PUBLIC_ZORA_UNIVERSAL_API_HTTP_URI!, {
        method: 'POST',
        body: JSON.stringify({
          query: operation.text,
          variables,
        }),
        headers,
        signal,
      })
      // check the response status code and throw if it's not a 2xx
      if (response.status < 200 || response.status >= 300) {
        throw new Error(`Failed to fetch: ${response.statusText}`)
      }
      // parse the response json
      return await response.json()
    } catch (error) {
      console.log(
        `[relay fetch error]: attempt: ${retries + 1}, operation: ${operation.name}`,
        error
      )
      lastError = error
      retries += 1
      // wait before retrying (don't wait on the first retry)
      if (retries < maxRetries && retries > 1) {
        await wait(config.retryDelay)
      }
    }
  } while (retries <= maxRetries)

  // Log the final error to Sentry if all retries failed
  if (lastError) {
    Sentry.captureException(lastError, {
      tags: {
        operation: operation.name,
        operationKind: operation.operationKind,
      },
      extra: {
        variables,
        retries,
        maxRetries,
      },
    })
  }

  // Return an empty data object instead of undefined to prevent "undefined is not an object" errors
  console.error('[relay fetch error]: ', lastError)

  // Create a structured error object that Relay can understand
  const relayError = {
    message: lastError instanceof Error ? lastError.message : 'Network request failed',
    name: 'RelayNetworkError',
    operation: operation.name,
    operationKind: operation.operationKind,
    variables,
  }

  // Log the relay-specific error to Sentry with additional context
  Sentry.captureException(
    new Error(`RelayNetwork: No data returned for operation \`${operation.name}\``),
    {
      tags: {
        operation: operation.name,
        operationKind: operation.operationKind,
        errorType: 'relay_network_error',
      },
      extra: {
        relayError,
        variables,
        retries,
        maxRetries,
      },
    }
  )

  return {
    data: null,
    errors: [
      {
        message:
          lastError instanceof Error ? lastError.message : 'Network request failed',
      },
    ],
  }
}

async function createHeaders(): Promise<HeadersInit> {
  const token = await getAccessToken()
  return {
    'content-type': 'application/json',
    ...(token ? { authorization: `Bearer ${token}` } : {}),
  }
}

async function wait(time: number) {
  await new Promise((resolve) => setTimeout(resolve, time))
}

/**
 * Creates a new Relay environment.
 */
export function createEnvironment(): Environment {
  const network = Network.create(fetchFn)
  const store = new Store(new RecordSource())
  return new Environment({
    store,
    network,
    relayFieldLogger: (event) => {
      console.log('fieldPath:', event.fieldPath, '\nevent:', event.kind)
    },
  })
}
